/**
 * @file ModalProvider.js
 * @module src/js/contexts/ModalProvider
 * @description
 * **Provides modal-related context to the EDALab frontend.**
 * - Manages the state of the currently displayed modal image (`modalImage`) and messages (`modalMessages`).
 * - Provides a function to update the modal image (`setModalImage`).
 * - Provides a function to show and remove modal messages (`showModalMessage`, `removeModalMessage`).
 * - Makes these values available via React Context to all child components.
 * - Please use `useModal()` to access the context in any component.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added `modalMessages` support for global toast messages. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added `id` to confirmDialog for proper handling of multiple dialogs. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { createContext, useCallback, useContext, useState } from 'react';

const ModalContext = createContext();

export default function ModalProvider({ children }) {
    const [modalImage, setModalImage] = useState(null);
    const [modalMessages, setModalMessages] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState(null);

    // Show a new message
    const showModalMessage = useCallback(({ type = 'info', content }) => {
        if (!content) return;
        const id = Date.now() + Math.random(); // simple unique id
        setModalMessages(prev => [...prev, { id, type, content }]);
    }, []);

    // Remove a message by id (called from each toast)
    const removeModalMessage = useCallback((id) => {
        setModalMessages(prev => prev.filter(m => m.id !== id));
    }, []);

    const confirm = useCallback(({
        hasInput = false,
        message,
        confirmLabel = 'OK',
        cancelLabel = 'Cancel',
        buttonColor,
        placeholder = '',
    } = {}) => {
        return new Promise((resolve) => {
            setConfirmDialog({
                id: crypto.randomUUID(),
                hasInput,
                message,
                confirmLabel,
                cancelLabel,
                buttonColor,
                placeholder,
                resolve,
            });
        });
    }, []);

    return (
        <ModalContext.Provider
            value={{
                modalImage,
                setModalImage,
                modalMessages,
                showModalMessage,
                removeModalMessage,
                confirmDialog,
                setConfirmDialog,
                confirm,
            }}
        >
            {children}
        </ModalContext.Provider>
    );
}

export const useModal = () => useContext(ModalContext);